import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry once on failure, but never retry 401/403 — those are auth errors,
      // not transient failures, and retrying just delays the redirect.
      retry: (failureCount, error) => {
        if (isAuthError(error)) return false;
        return failureCount < 1;
      },

      // Don't hammer the server when the tab regains focus — the user didn't
      // ask for fresh data, and most of our data isn't that time-sensitive.
      refetchOnWindowFocus: false,

      // Don't refetch just because the network came back; wait for the user
      // to trigger a new query or navigate.
      refetchOnReconnect: false,

      // Keep data fresh for 1 minute before it's considered stale.
      staleTime: 60_000,

      // Keep unused query data in the cache for 5 minutes before garbage
      // collection — covers normal back-navigation without re-fetching.
      gcTime: 5 * 60_000,
    },
    mutations: {
      // Mutations are one-shot; never auto-retry. A failed POST/PUT/DELETE
      // may have partially succeeded — caller decides whether to retry.
      retry: false,
    },
  },
});

const AUTH_ERROR_STATUSES = new Set([401, 403]);

function isAuthError(error: unknown): boolean {
  if (error instanceof Error && "status" in error) {
    return AUTH_ERROR_STATUSES.has((error as Error & { status: number }).status);
  }
  // axios errors surface status on error.response.status
  if (
    error !== null &&
    typeof error === "object" &&
    "response" in error &&
    error.response !== null &&
    typeof error.response === "object" &&
    "status" in error.response
  ) {
    return AUTH_ERROR_STATUSES.has(
      (error.response as { status: number }).status,
    );
  }
  return false;
}
